import { walletKeys } from '@/constant/queryKeys';
import { useAuthStore } from '@/hooks/useAuthStore';
import { apiRequestV2, queryClient } from '@/lib/queryClient';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

export type WalletList = {
    wallets: Array<{
        id: number;
        publicKey: string;
        walletType: 'generated' | 'connected';
        walletAdapter: string | null;
        isPrimary: boolean;
        createdAt: string;
        balanceLamports?: number;
        balanceSol?: number;
        error?: string;
    }>;
}

export interface Wallet {
    id: number;
    publicKey: string;
    walletType: 'generated' | 'connected';
    walletAdapter: string | null;
    isPrimary: boolean;
    createdAt: string;
    balanceLamports?: number;
    balanceSol?: number;
    error?: string;
}

export function postConnectWalletOptions() {
    return mutationOptions({
        mutationKey: walletKeys.postConnectWallet,
        mutationFn: (request: { publicKey: string, walletAdapter: string | null}) => apiRequestV2<{
            message: string;
            wallet: {
                id: number;
                userId: number;
                publicKey: string;
                isPrimary: boolean;
                createdAt: string;
                updatedAt: string;
                walletAdapter: string | null;
            };
        }>('POST', '/api/v1/wallet/connect', request),
    });
}

export function getWalletBalanceOptions(publicKey?: string, walletExists: boolean = true) {
    return queryOptions({
        queryKey: walletKeys.getWalletBalance(publicKey),
        queryFn: () => apiRequestV2<{
            publicKey: string;
            balanceLamports: number;
            balanceSol: number;
        }>('GET', `/api/v1/wallet/balance/${publicKey}`),
        staleTime: 0,
        placeholderData: (previousData) => previousData,
        enabled: !!publicKey && walletExists,
    });
}

export function deleteWalletOptions() {
    return mutationOptions({
        mutationKey: walletKeys.deleteWallet,
        mutationFn: (walletId: number) => apiRequestV2<{
            message: string;
            wallets: Array<{
            id: number;
            publicKey: string;
            isPrimary: boolean;
            createdAt: string;
            updatedAt: string;
            walletAdapter: string | null;
            }>;
        }>('DELETE', `/api/v1/wallet/${walletId}`),
    });
}

export function getAllWalletBalancesOptions(sessionValidated: boolean, isAuthenticated: boolean) {
    return queryOptions({
        queryKey: walletKeys.getAllWalletBalances,
        queryFn: () => apiRequestV2<{
            wallets: Array<{
                id: number;
                publicKey: string;
                isPrimary: boolean;
                balanceLamports?: number;
                balanceSol?: number;
                error?: string;
            }>;
        }>('GET', `/api/v1/wallet/balances`),
        staleTime: 0,
        placeholderData: (previousData) => previousData,
        enabled: sessionValidated && isAuthenticated,
    });
}

export function getWalletListOptions(sessionValidated: boolean, isAuthenticated: boolean) {
    return queryOptions({
        queryKey: walletKeys.getWalletList,
        queryFn: () => apiRequestV2<WalletList>('GET', `/api/v1/wallet/list`),
        staleTime: 30000,
        placeholderData: (previousData) => previousData,
        enabled: sessionValidated && isAuthenticated,
    });
}

export function getTokenStatsOptions(tokenName: string) {
    const { isAuthenticated } = useAuthStore();
    return queryOptions({
        queryKey: walletKeys.tokenStats(tokenName),
        queryFn: () => apiRequestV2<{
            tokenName: string;
            balance: number;
            changePercentage: number;
            changeText: string;
        }>('GET', `/api/v1/wallet/token-stats/${tokenName}`),
        staleTime: 60000,
        placeholderData: (previousData) => previousData,
        enabled: !!tokenName && isAuthenticated,
    });
}

export function getPrimaryWalletOptions(sessionValidated: boolean, isAuthenticated: boolean) {
    return queryOptions({
        queryKey: walletKeys.getPrimaryWallet,
        queryFn: () => apiRequestV2<{
            wallet: {
                id: number;
                publicKey: string;
                walletType: 'generated' | 'connected';
                walletAdapter: string | null;
                isPrimary: boolean;
                createdAt: string;
                balanceLamports?: number;
                balanceSol?: number;
                error?: string;
            };
        }>('GET', `/api/v1/wallet/primary`),
        staleTime: 30000,
        placeholderData: (previousData) => previousData,
        enabled: sessionValidated && isAuthenticated,
    });
}

export function postPrimaryWalletOptions(walletListQuery?: WalletList) {
    return mutationOptions({
        mutationKey: ['wallet', 'set-primary'],
        mutationFn: (walletId: number) => apiRequestV2<{
            message: string;
            wallet: {
                id: number;
                publicKey: string;
                walletType: 'generated' | 'connected';
                walletAdapter: string | null;
                isPrimary: boolean;
                createdAt: string;
            };
        }>('POST', '/api/v1/wallet/set-primary', { walletId }),
        onMutate: async (walletId: number) => {
            // Cancel ongoing refetches
            await queryClient.cancelQueries({ queryKey: walletKeys.getWalletList });
            await queryClient.cancelQueries({ queryKey: walletKeys.getPrimaryWallet });

            // Snapshot current data
            const previousWalletList = queryClient.getQueryData(walletKeys.getWalletList);
            const previousPrimaryWallet = queryClient.getQueryData(walletKeys.getPrimaryWallet);

            // Optimistically update wallet list
            queryClient.setQueryData(walletKeys.getWalletList, (old: any) => {
                if (!old?.wallets) return old;
                return {
                    ...old,
                    wallets: old.wallets.map((w: Wallet) => ({
                        ...w,
                        isPrimary: w.id === walletId,
                    })),
                };
            });

            // Optimistically update primary wallet
            const newPrimary = walletListQuery?.wallets.find(w => w.id === walletId);
            if (newPrimary) {
                queryClient.setQueryData(walletKeys.getPrimaryWallet, {
                    wallet: { ...newPrimary, isPrimary: true },
                });
            }

            return { previousWalletList, previousPrimaryWallet };
        },
        onError: (err, variables, context) => {
            // Rollback on error
            if (context?.previousWalletList) {
                queryClient.setQueryData(walletKeys.getWalletList, context.previousWalletList);
            }
            if (context?.previousPrimaryWallet) {
                queryClient.setQueryData(walletKeys.getPrimaryWallet, context.previousPrimaryWallet);
            }
        },
        onSuccess: () => {
            // Invalidate to refetch fresh data
            queryClient.invalidateQueries({ queryKey: walletKeys.getWalletList });
            queryClient.invalidateQueries({ queryKey: walletKeys.getPrimaryWallet });
        },
    });
}
